/** Reconnects delivery using event cursors. It never submits or executes another turn. */
export function resumableGatewayResponse(initial: Response, reconnect: (after: number, signal: AbortSignal) => Promise<Response>, parentSignal?: AbortSignal): Response {
  if (initial.headers.get('x-attune-agent-backend') !== 'gateway') return initial;
  const controller = new AbortController();
  const signal = parentSignal ? AbortSignal.any([parentSignal, controller.signal]) : controller.signal;
  let currentReader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  const encoder = new TextEncoder();
  const frames = (async function* () {
    let response = initial;
    let cursor = 0;
    let retries = 0;
    while (!signal.aborted) {
      let terminal = false;
      try {
        if (!response.ok || !response.body) {
          if (response.status >= 400 && response.status < 500) {
            const payload = await response.json().catch(() => null);
            throw new ReplayAccessError(payload?.error?.message || 'Access to this conversation changed.');
          }
          throw new Error('Gateway stream unavailable.');
        }
        currentReader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!signal.aborted) {
          const { done, value } = await currentReader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let boundary: number;
          while ((boundary = buffer.indexOf('\n\n')) >= 0) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            const id = /^id: (\d+)$/m.exec(frame);
            if (id && Number(id[1]) <= cursor) continue;
            if (id) { cursor = Number(id[1]); retries = 0; }
            yield encoder.encode(`${frame}\n\n`);
            if (/^data: \[DONE\]$/m.test(frame)) { terminal = true; return; }
          }
        }
      } catch (error) {
        if (error instanceof ReplayAccessError) throw error;
        if (signal.aborted) return;
      } finally {
        await currentReader?.cancel().catch(() => undefined);
        currentReader?.releaseLock();
        currentReader = undefined;
      }
      if (terminal || signal.aborted) return;
      await new Promise<void>(resolve => {
        const timer = setTimeout(done, Math.min(2_000, 100 * 2 ** Math.min(retries++, 5)));
        function done() { clearTimeout(timer); signal.removeEventListener('abort', done); resolve(); }
        signal.addEventListener('abort', done, { once: true });
        if (signal.aborted) done();
      });
      if (signal.aborted) return;
      try { response = await reconnect(cursor, signal); }
      catch { if (signal.aborted) return; response = new Response(null, { status: 503 }); }
    }
  })();
  return new Response(new ReadableStream<Uint8Array>({
    async pull(target) {
      try { const next = await frames.next(); if (next.done) target.close(); else target.enqueue(next.value); }
      catch (error) { target.error(error); }
    },
    async cancel() { controller.abort(); await currentReader?.cancel().catch(() => undefined); await frames.return(); },
  }), { status: initial.status, headers: initial.headers });
}

class ReplayAccessError extends Error {}
